import { usePermissions } from '@/shared/hooks/usePermissions';
import { USER_PERMISSIONS } from '@/shared/types/permissions';
import {
  cilBullhorn,
  cilCart,
  cilHistory,
  cilPeople,
  cilReload,
  cilSettings,
  cilSync,
  cilTrash,
  cilUser,
  cilWarning,
} from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import React from 'react';

export interface NavItemConfig {
  label: string;
  to: string;
  icon: React.ReactNode;
  show?: () => boolean;
}

export interface NavGroupConfig {
  label: string;
  items: NavItemConfig[];
}

export const useMenuGroups = (): NavGroupConfig[] => {
  const { hasPermission } = usePermissions();
  const hasAnyProductPermission = [
    USER_PERMISSIONS.ProductsView,
    USER_PERMISSIONS.ProductsCreate,
    USER_PERMISSIONS.ProductsImagesEdit,
    USER_PERMISSIONS.ProductsSubscriptionConfigurationEdit,
    USER_PERMISSIONS.ProductsPromotionsBadgesEdit,
    USER_PERMISSIONS.ProductsVarietyPackContentsEdit,
    USER_PERMISSIONS.ProductsAdditionalInformationEdit,
  ].some(hasPermission);

  return [
    {
      label: 'Customers',
      items: [
        {
          label: 'Customers',
          to: '/customers',
          icon: <CIcon icon={cilUser} customClassName="nav-icon" />,
          show: () => hasPermission(USER_PERMISSIONS.CustomersView),
        },
      ],
    },
    {
      label: 'Products',
      items: [
        {
          label: 'Products',
          to: '/products',
          icon: <CIcon icon={cilCart} customClassName="nav-icon" />,
          show: () => hasAnyProductPermission,
        },
        {
          label: 'Product Ordering',
          to: '/product-ordering',
          icon: <CIcon icon={cilSettings} customClassName="nav-icon" />,
          show: () => hasPermission(USER_PERMISSIONS.ProductsView),
        },
        {
          label: 'Replace Products in Subscriptions',
          to: '/replacement-batches',
          icon: <CIcon icon={cilReload} customClassName="nav-icon" />,
          show: () => hasPermission(USER_PERMISSIONS.ReplacementBatchesView),
        },
        {
          label: 'Remove Products from Subscriptions',
          to: '/removal-batches',
          icon: <CIcon icon={cilTrash} customClassName="nav-icon" />,
          show: () => hasPermission(USER_PERMISSIONS.RemovalBatchesView),
        },
      ],
    },
    {
      label: 'Promotions',
      items: [
        {
          label: 'Upsell Configuration',
          to: '/upsell-config',
          icon: <CIcon icon={cilSettings} customClassName="nav-icon" />,
          show: () => hasPermission(USER_PERMISSIONS.UpsellsConfigurationView),
        },
        {
          label: 'Subscription Discounts',
          to: '/discounts',
          icon: <CIcon icon={cilBullhorn} customClassName="nav-icon" />,
          show: () =>
            hasPermission(USER_PERMISSIONS.SubscriptionDiscountsView) ||
            hasPermission(USER_PERMISSIONS.SubscriptionDiscountsEdit),
        },
        {
          label: 'RAF Settings',
          to: '/raf-settings',
          icon: <CIcon icon={cilSettings} customClassName="nav-icon" />,
          show: () => hasPermission(USER_PERMISSIONS.RafConfigurationView),
        },
        {
          label: 'Deep Links Management',
          to: '/deep-links-management',
          icon: <CIcon icon={cilSettings} customClassName="nav-icon" />,
          show: () =>
            hasPermission(USER_PERMISSIONS.DeepLinksView) ||
            hasPermission(USER_PERMISSIONS.DeepLinksEdit),
        },
      ],
    },
    {
      label: 'Users',
      items: [
        {
          label: 'Nero Users',
          to: '/users',
          icon: <CIcon icon={cilPeople} customClassName="nav-icon" />,
          show: () => hasPermission(USER_PERMISSIONS.UsersView),
        },
        {
          label: 'Roles Management',
          to: '/users/roles',
          icon: <CIcon icon={cilPeople} customClassName="nav-icon" />,
          show: () =>
            hasPermission(USER_PERMISSIONS.UserRolesView) ||
            hasPermission(USER_PERMISSIONS.UserRolesEdit),
        },
        {
          label: 'Direct Sales Agents',
          to: '/ds-agents',
          icon: <CIcon icon={cilPeople} customClassName="nav-icon" />,
          show: () =>
            hasPermission(USER_PERMISSIONS.DSAgentsView) ||
            hasPermission(USER_PERMISSIONS.DSAgentsEdit),
        },
      ],
    },
    {
      label: 'Logs',
      items: [
        {
          label: 'Trial Funnel Errors',
          to: '/trial-funnel-logs',
          icon: <CIcon icon={cilHistory} customClassName="nav-icon" />,
          show: () => hasPermission(USER_PERMISSIONS.TrialFunnelLogsView),
        },
        {
          label: 'Audit Logs',
          to: '/audit-logs',
          icon: <CIcon icon={cilHistory} customClassName="nav-icon" />,
          show: () => hasPermission(USER_PERMISSIONS.AuditLogView),
        },
        {
          label: 'Broken Subscriptions',
          to: '/broken-subscriptions',
          icon: <CIcon icon={cilWarning} customClassName="nav-icon" />,
          show: () =>
            hasPermission(USER_PERMISSIONS.BrokenSubscriptionsView) ||
            hasPermission(USER_PERMISSIONS.BrokenSubscriptionsEdit),
        },
      ],
    },
    {
      label: 'System',
      items: [
        {
          label: 'Banners',
          to: '/banners',
          icon: <CIcon icon={cilBullhorn} customClassName="nav-icon" />,
          show: () => hasPermission(USER_PERMISSIONS.BannersView),
        },
        {
          label: 'System Admin Actions',
          to: '/system-admin-actions',
          icon: <CIcon icon={cilSync} customClassName="nav-icon" />,
          show: () => hasPermission(USER_PERMISSIONS.SystemAdmin),
        },
      ],
    },
  ];
};

export const useNavItems = (): NavItemConfig[] => {
  return useMenuGroups().flatMap((group) => group.items);
};
